import { useCallback, useMemo, useState } from "react";
import { PixelRatio, StyleSheet } from "react-native-web";
import { tokens } from "@opendoor/tokens-studio/dist/bricks-next/tokens/Components";
import { getTokenValue, isAndroid, isWeb, useProps } from "@tamagui/core";
import { ChevronDown, ChevronUp } from "../../../__generated__/icons";
import { getFontName, getTypographyValue } from "../../../helpers/fontTokens";
import { isKeyOfObject } from "../../../helpers/utils";
import { TrackableContainer } from "../../shared/TrackableContainer";
import { useInputId, useInputState } from "../shared/hooks";
import { InputError } from "../shared/InputError";
import { InputFrame } from "../shared/InputFrame";
import { InputLabel } from "../shared/InputLabel";
import { PickerSelect } from "./PickerSelect";
import { jsx, jsxs } from "react/jsx-runtime";
const { textInput: textInputTokens } = tokens, Dropdown = ({
  analyticsName,
  trackImpression,
  title,
  supportText,
  required,
  errorText: externalErrorText,
  invalid: externalInvalid,
  disabled,
  id,
  name,
  onFocus,
  onBlur: externalOnBlur,
  onOpen,
  onClose,
  onValueChange,
  options,
  placeholderOption,
  prefilledValue,
  customErrorMessages,
  onValidationChange,
  ...propsIn
}) => {
  const { size = "medium", ...props } = useProps(propsIn), pickerId = useInputId(id), items = useMemo(() => options.map(({ label, value: value2 }) => ({ label, value: value2, key: value2 })), [options]), placeholder = useMemo(() => ({
    label: placeholderOption?.label ?? "Select an item",
    value: placeholderOption?.value ?? "",
    key: "placeholder"
  }), [placeholderOption?.label, placeholderOption?.value]), [value, setValue] = useState(prefilledValue ?? placeholder.value), [internalErrorText, setInternalErrorText] = useState(), [internalValidationState, setInternalValidationState] = useState("unknown"), validate = useCallback(
    (value2) => {
      let errorText2, validationState = "valid";
      return value2 === "" && required && (errorText2 = customErrorMessages?.fieldRequired ?? "This field is required", validationState = "invalid"), setInternalErrorText(errorText2), setInternalValidationState(validationState), validationState;
    },
    [required, customErrorMessages?.fieldRequired]
  ), handleValueChange = useCallback(
    (val) => {
      const value2 = typeof val == "string" ? val : "";
      setValue(value2), onValueChange && onValueChange(value2);
      const validationState = validate(value2);
      onValidationChange && onValidationChange(validationState);
    },
    [validate, onValueChange, onValidationChange]
  ), onBlur = useCallback(
    (e) => {
      validate(value), externalOnBlur && externalOnBlur(e);
    },
    [value, validate, externalOnBlur]
  ), errorText = externalErrorText ?? internalErrorText, invalid = !!errorText || externalInvalid || internalValidationState === "invalid", { state, handleBlur, handleFocus, setFocused } = useInputState({
    errorText,
    invalid,
    disabled,
    onFocus,
    onBlur
  }), handleOpen = useCallback(() => {
    setFocused(!0), onOpen && onOpen();
  }, [setFocused, onOpen]), handleClose = useCallback(() => {
    setFocused(!1), validate(value), onClose && onClose();
  }, [setFocused, validate, value, onClose]), {
    frameHeight,
    inputStyles,
    viewContainerStyles,
    placeholderStyles,
    iconContainerStyles,
    iconStyles
  } = useMemo(() => getStyles({ size, state }), [size, state]);
  return /* @__PURE__ */ jsxs(
    TrackableContainer,
    {
      analyticsName,
      trackImpression,
      eventName: "inputViewEvent",
      gap: textInputTokens.padding.gap.textAndField[size],
      children: [
        /* @__PURE__ */ jsx(
          InputLabel,
          {
            size,
            title,
            supportText,
            id: pickerId,
            required,
            state
          }
        ),
        /* @__PURE__ */ jsx(InputFrame, { size, state, height: frameHeight, children: /* @__PURE__ */ jsx(
          PickerSelect,
          {
            ...props,
            items,
            disabled,
            placeholder,
            value,
            onValueChange: handleValueChange,
            pickerProps: {
              id: pickerId,
              onFocus: handleFocus,
              onBlur: handleBlur,
              ...isWeb ? {
                "aria-invalid": state === "error",
                "aria-required": required,
                name
              } : {}
            },
            onOpen: handleOpen,
            onClose: handleClose,
            useNativeAndroidPickerStyle: !1,
            fixAndroidTouchableBug: !0,
            style: {
              viewContainer: viewContainerStyles,
              inputWeb: {
                ...inputStyles,
                ...value === placeholder.value ? placeholderStyles : {},
                outlineStyle: "none",
                cursor: state === "inactive" ? "default" : "pointer",
                appearance: "none",
                textOverflow: "ellipsis"
              },
              inputIOS: inputStyles,
              chevronUp: { display: "none" },
              chevronDown: { display: "none" },
              inputAndroid: inputStyles,
              headlessAndroidContainer: StyleSheet.absoluteFill,
              iconContainer: iconContainerStyles,
              placeholder: placeholderStyles
            },
            touchableWrapperProps: isAndroid ? { style: viewContainerStyles } : {},
            Icon: () => (
              // This toggles the chevron icon on ios/android. On web, there doesn't
              // seem to be a good way to know if the picker is open because it's
              // possible to focus the picker without opening it and `select`
              // doesn't have an event corresponding to open/close.
              !isWeb && state === "focused" ? /* @__PURE__ */ jsx(ChevronUp, { ...iconStyles }) : /* @__PURE__ */ jsx(ChevronDown, { ...iconStyles })
            )
          }
        ) }),
        /* @__PURE__ */ jsx(InputError, { errorText })
      ]
    }
  );
}, getStyles = ({ size, state }) => {
  const typopgraphyToken = textInputTokens.typography.valueText[size], colorToken = isKeyOfObject(state, textInputTokens.color.content.valueText.state) ? textInputTokens.color.content.valueText.state[state] : textInputTokens.color.content.valueText.default, paddingVerticalToken = textInputTokens.padding.topBottom[size], paddingHorizontalToken = textInputTokens.padding.leftRight[size], borderWidthToken = textInputTokens.borderWidth.default, paddingVertical = getTokenValue(paddingVerticalToken, "space"), paddingHorizontal = getTokenValue(paddingHorizontalToken, "space"), borderWidth = getTokenValue(borderWidthToken, "space"), lineHeight = getTypographyValue(typopgraphyToken, "lineHeight"), fontScale = PixelRatio.getFontScale(), frameHeight = lineHeight * fontScale + 2 * paddingVertical + 2 * borderWidth, viewContainerStyles = {
    position: "absolute",
    top: -borderWidth,
    bottom: -borderWidth,
    left: -borderWidth,
    right: -borderWidth,
    zIndex: 1
  }, iconContainerStyles = {
    top: 0,
    bottom: 0,
    right: paddingHorizontal + borderWidth,
    justifyContent: "center",
    zIndex: -1
  }, fontWeight = getTypographyValue(typopgraphyToken, "weight"), iconSize = size === "small" || size === "xsmall" ? 20 : 24, iconGap = getTokenValue(textInputTokens.padding.gap.textAndIcon.default, "space"), inputStyles = {
    fontFamily: getFontName(fontWeight),
    fontSize: getTypographyValue(typopgraphyToken, "size"),
    fontWeight,
    letterSpacing: getTypographyValue(typopgraphyToken, "letterSpacing"),
    color: getTokenValue(colorToken, "color"),
    borderWidth,
    borderColor: "transparent",
    backgroundColor: "transparent",
    height: frameHeight,
    paddingVertical,
    paddingLeft: paddingHorizontal,
    paddingRight: paddingHorizontal + iconSize + iconGap
  }, placeholderStyles = {
    color: getTokenValue(textInputTokens.color.content.placeholderText.default, "color")
  };
  return {
    frameHeight,
    viewContainerStyles,
    iconContainerStyles,
    inputStyles,
    placeholderStyles,
    iconStyles: {
      size: iconSize,
      color: colorToken
    }
  };
};
export {
  Dropdown
};
